<script setup lang="ts">
import CircularSlider from '@/components/CircularSlider.vue';
import { useToastStore } from '@/stores/toast.store';
import { formatAmount, formatDateFrench, generateUUID, MONTHS_FR } from '@/utils';
import UserPlanMensuel from '@/views/users/@components/UserPlanMensuel.vue';
import axios from 'axios';
import moment from 'moment';
import { inject, onMounted, reactive, Ref, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const toastStore = useToastStore();
const loadingSimulation = ref(false);
const prospect = ref(route.meta.item as any);
const prelevementPourcentInput = ref(prospect.value.quotite_cessible_max ?? 20);
const souscription = reactive({} as any);
const planMensuelDatas = reactive({ id: null as any, plan_mensuel: [] as any, title: null });

onMounted(() => {
    calculateSimulation();
})

function onCursorChanged() {
    calculateSimulation();
}

async function calculateSimulation() {
    try {
        loadingSimulation.value = true;
        const response = await axios.post(`/backoffice/prospects/${prospect.value.id}/simulate_souscription`, { prelevementPourcent: prelevementPourcentInput.value });
        Object.assign(souscription, response.data.souscription);
        Object.assign(planMensuelDatas, {
            id: generateUUID(),
            plan_mensuel: response.data.plan_mensuel,
            title: getTitlePlanMensuel(souscription.date_debut, souscription.date_fin, 0)
        });
    } catch(error: any) {
        toastStore.error(error.message || 'Une erreur est survenue lors du rejet des prospects.');
    } finally {
        loadingSimulation.value = false;
    }
}

function getTitlePlanMensuel(dateDebut: string, dateFin: string, offset: number) {
    const dateDebutM = moment(dateDebut);
    const dateFinM = moment(dateFin);

    return `Année ${(offset + 1)} / ${MONTHS_FR[dateDebutM.month()]} ${dateDebutM.format('YYYY')} - ${MONTHS_FR[dateFinM.month()]} ${dateFinM.format('YYYY')}`;
}

function calculateAnciennete(dateEngagement: string | null): string {
    if (!dateEngagement) return '-';
    try {
        const engagementDate = moment(dateEngagement);
        if (!engagementDate.isValid()) return '-';
        const now = moment();
        const years = now.diff(engagementDate, 'years');
        const months = now.diff(engagementDate, 'months') % 12;
        
        if (years > 0 && months > 0) {
            return `${years} an${years > 1 ? 's' : ''} ${months} mois`;
        } else if (years > 0) {
            return `${years} an${years > 1 ? 's' : ''}`;
        } else if (months > 0) {
            return `${months} mois`;
        } else {
            return '0 mois';
        }
    } catch {
        return '-';
    }
}

</script>
<template>
    <section class="container-full-1 bg-grey-50" style="margin: 0.5vh 0;">

        <section class="s20 flex gap-2 py-1 position-relative" style="z-index: 1000; height: 40.5vh;">
            <section class="s10 widget rounded-20 bg-gradient-dark flex flex-col justify-content-center h-100p px-7 py-7">
                <div class="s20 items-center gap-6 pb-6 collapse-content-flex active">
                    <div class="s4_mobile px-4 text-center">
                        <div class="rounded-100 overflow-hidden item active"><!--active-->
                            <img src="/assets/users_avatar/600w/user.jpg" width="100%" />
                        </div>
                        <div class="clear-both h-15"></div>
                        <span class="tag-state warning stroke">En cours</span>
                        <span class="tag-state notif stroke">Actif</span>
                        <h3 class="mt-5 text-center">C788PMKJ</h3>
                        <p class="flex gap-2 text-grey-200 my-3"><img src="/icons/icon-calendrier.svg" class="size-xs" /> Dernière connexion: 13/12/2025</p>
                    </div>
                    <div class="s7_mobile">
                        <h3>{{ prospect.nom + ' ' + prospect.prenom }}</h3>
                        <h4 class="font-normal mt-2 mb-3">{{ prospect.collectivite.nom }}</h4>
                        <h6 class="font-semibold my-2">{{ prospect.telephone || 'N/A' }}</h6>
                        <h6 class="font-semibold my-2">{{ prospect.email || 'N/A' }}</h6>
                        <h4 class="font-normal mt-4 mb-4">
                            Date de naissance <br class="mb-3"/>
                            <b>{{ formatDateFrench(prospect.date_naissance) }} - {{ calculateAnciennete(prospect.date_naissance) }}</b>
                        </h4>
                        <hr class="hr-white"/>
                        <h4 class="font-normal mt-2 mb-3">{{ prospect.identifiant_ag }}</h4>
                        <h5 class="font-normal mt-2 mb-3">{{ prospect.fonction_ag }}</h5>
                        <h5 class="font-normal mt-2 mb-5">{{ prospect.cpte_fonct_ag + ' / ' + prospect.categ_ag }}</h5>
                        <hr class="hr-white"/>
                        <h6 class="font-normal mb-4">
                            Date d'engagement <br class="mb-2"/>
                            <b>{{ formatDateFrench(prospect.date_engagement) }} - {{ calculateAnciennete(prospect.date_engagement) }}</b>
                        </h6>
                    </div>
                    <div>
                        <hr class="hr-vertical hr-vertical-white h-100p"/>
                    </div>
                    <div class="s8_mobile px-6 mt-2">
                        <h4 class="font-normal mb-4">
                            Av. retraite 
                            <br class="mb-3"/><b>{{ prospect.eta_retraite }} année(s)</b>
                        </h4>
                        <h5 class="font-normal mb-4">
                            Date d'import <br class="mb-2"/>
                            <b class="text-xl">{{ formatDateFrench(prospect.created_at) }}</b>
                        </h5>
                        <h5 class="font-normal mb-4">
                            Date de fin de souscription <br class="mb-2"/>
                            <b class="text-xl">{{ souscription?.date_fin ? formatDateFrench(souscription.date_fin) : 'N/A' }}</b>
                        </h5>
                        <hr class="hr-white"/>
                        <h5 class="font-normal mb-4">
                            Utilisation total <br class="mb-2"/>
                            <b class="text-xl">{{ souscription?.utilisation_total !== undefined ? formatAmount(souscription.utilisation_total) : 'N/A' }}</b>
                        </h5>
                        <h5 class="font-normal mb-4">
                            Salaire Net après Utilisation <br class="mb-2"/>
                            <b class="text-xl">{{ souscription?.salaire_net !== undefined ? formatAmount(souscription.salaire_net) : 'N/A' }}</b>
                        </h5>
                        <h5 class="font-normal mb-4">
                            Quotité cessible disponible <br class="mb-2"/>
                            <b class="text-xl">{{ souscription?.quotite_cessible_montant !== undefined ? formatAmount(souscription.quotite_cessible_montant) : 'N/A' }}</b>
                        </h5>
                    </div>
                </div>

                <div class="clear-both h-0"></div>
                <!--Barre de progression - Component-->
                <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-white-rgb), 0.1);">
                    <div class="widget-floating py-0 text-xl flex-right">Utilisation</div>
                    <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-green" :style="`width: ${souscription?.prelevement_pourcent !== undefined ? formatAmount(souscription.prelevement_pourcent) : 0}%;`">
                        {{ souscription?.prelevement_pourcent !== undefined ? formatAmount(souscription.prelevement_pourcent) : 0 }}%
                    </div>
                </div>
                <!--End Barre de progression-->
            </section>

            <section class="s10 h-100p widget rounded-20 bg-gradient-dark flex align-items-center justify-content-center gap-5 px-6 py-5">
                <div class="s8_mobile h-100p px-7">
                    <div class="container-full-2 s20 mb-0">
                        <h3 class="font-semibold mt-5 text-center">Simulateur de souscription</h3>
                    </div>

                    <circular-slider
                        v-model="prelevementPourcentInput"
                        @change="onCursorChanged"
                        :min="souscription?.quotite_cessible_min !== undefined ? souscription.quotite_cessible_min : 0" 
                        :max="souscription?.quotite_cessible_max !== undefined ? souscription.quotite_cessible_max : 100" :step="1"
                        :size="380" :stroke-width="18"
                        :keepBaseScale="true"
                        color="#5CE6A6" track-color="rgba(92,230,166,0.25)"
                        unit="%"
                        />

                    <h1 class="secondary-font font-bold text-white text-center">{{ souscription?.prelevement_montant !== undefined ? formatAmount(souscription.prelevement_montant) : 'N/A' }}</h1>
                    <h4 class="font-semibold mt-3 text-center">Prélèvement sur salaire</h4>
                </div>
                <div class="s11_mobile mt-2">
                    <div class="grid2">
                        <div class="widget shadow-200 flex align-items-center rounded-30 px-7 py-5" style="background-color: rgba(var(--color-black-rgb), 0.5);">
                            <div class="s6">
                                <h2 class="secondary-font font-normal text-grey-100">Net</h2>
                                <h4 class="secondary-font font-normal text-grey-100"></h4>
                            </div>
                            <div class="s14">
                                <h6 class="secondary-font font-medium text-grey-100 mb-1">Salaire</h6>
                                <h2 class="secondary-font font-bold text-primary-color">{{ souscription?.salaire !== undefined ? formatAmount(souscription.salaire) : 'N/A' }}</h2>
                            </div>
                        </div>
                        <!--CardQuotite-->
                        <div class="widget shadow-200 flex align-items-center rounded-30 px-7 py-5" style="background-color: rgba(var(--color-black-rgb), 0.8);">
                            <div class="s6">
                                <h2 class="secondary-font font-normal text-grey-100">Par</h2>
                                <h4 class="secondary-font font-normal text-grey-100">mois</h4>
                            </div>
                            <div class="s14">
                                <h6 class="secondary-font font-medium text-grey-100 mb-1">Mon revolving</h6>
                                <h2 class="secondary-font font-bold text-primary-color">{{ souscription?.solde_utilisable !== undefined ? formatAmount(souscription.solde_utilisable) : 'N/A' }}</h2>
                            </div>
                        </div>
                        <!--End CardQuotite-->
                        <!--CardQuotite-->
                        <div class="widget shadow-200 flex align-items-center rounded-30 px-7 py-5" style="background-color: rgba(var(--color-black-rgb), 0.8);">
                            <div class="s6">
                                <h2 class="secondary-font font-normal text-grey-100">12</h2>
                                <h4 class="secondary-font font-normal text-grey-100">mois</h4>
                            </div>
                            <div class="s14">
                                <h6 class="secondary-font font-medium text-grey-100 mb-1">Revolving sur 12 mois</h6>
                                <h2 class="secondary-font font-bold text-primary-color">{{ souscription?.pouvoir_achat_an !== undefined ? formatAmount(souscription.pouvoir_achat_an) : 'N/A' }}</h2>
                            </div>
                        </div>
                        <!--End CardQuotite-->
                        <!--CardQuotite-->
                        <div class="widget shadow-200 flex align-items-center rounded-30 px-7 py-5" style="background-color: rgba(var(--color-black-rgb), 0.8);">
                            <div class="s6">
                                <h2 class="secondary-font font-normal text-grey-100">36</h2>
                                <h4 class="secondary-font font-normal text-grey-100">mois</h4>
                            </div>
                            <div class="s14">
                                <h6 class="secondary-font font-medium text-grey-100 mb-1">Revolving sur 36 mois</h6>
                                <h2 class="secondary-font font-bold text-primary-color">{{ souscription?.pouvoir_achat !== undefined ? formatAmount(souscription.pouvoir_achat) : 'N/A' }}</h2>
                            </div>
                        </div>
                        <!--End CardQuotite-->
                        <!--CardQuotite - Component-->
                        <div class="widget shadow-200 flex align-items-center rounded-30 px-7 py-5" style="background-color: rgba(var(--color-black-rgb), 0.5);">
                            <div class="s6">
                                <h2 class="secondary-font font-normal text-grey-100">{{ souscription?.prelevement_pourcent !== undefined ? souscription?.prelevement_pourcent + '%' : 'N/A' }}</h2>
                                <h4 class="secondary-font font-normal text-grey-100"></h4>
                            </div>
                            <div class="s14">
                                <h6 class="secondary-font font-medium text-grey-100 mb-1">Prélèvement sur salaire</h6>
                                <h2 class="secondary-font font-bold text-primary-color">{{ souscription?.prelevement_montant !== undefined ? formatAmount(souscription.prelevement_montant) : 'N/A' }}</h2>
                            </div>
                        </div>
                        <!--End CardQuotite-->
                        <!--CardQuotite-->
                        <div class="widget shadow-200 flex align-items-center rounded-30 px-7 py-5" style="background-color: rgba(var(--color-black-rgb), 0.5);">
                            <div class="s6">
                                <h2 class="secondary-font font-normal text-grey-100">36</h2>
                                <h4 class="secondary-font font-normal text-grey-100">mois</h4>
                            </div>
                            <div class="s14">
                                <h6 class="secondary-font font-medium text-grey-100 mb-1">Prélèvement sur 36 mois</h6>
                                <h2 class="secondary-font font-bold text-primary-color">{{ souscription?.prelevement_montant_3_an !== undefined ? formatAmount(souscription.prelevement_montant_3_an) : 'N/A' }}</h2>
                            </div>
                        </div>
                        <!--End CardQuotite-->

                    </div>
                    <!----->
                </div>
            </section>
        </section>

        <section class="s20 flex gap-2 py-1" style="height: 58.5vh;">
            <section class="s10 widget border rounded-20 shadow-100 pb-1 h-100p" style="overflow-y: scroll;">
                <template v-if="planMensuelDatas.id">
                    <user-plan-mensuel :plan-mensuel="planMensuelDatas.plan_mensuel!" :title="planMensuelDatas.title!" />
                </template>
            </section>
            
            <section class="s10 widget border rounded-20 h-100p px-6 pt-4 pb-1">
                <div class="container-full-2 s20 mb-5">
                    <h4 class="font-semibold mt-3">Carte / Utilisation / Données</h4>
                </div>
                <div class="s10_mobile px-4">
                    <!--CardCarte-->
                    <div class="card-carte">
                        <div class="card-carte-floating">
                            <div class="s20">
                                <h5 class="secondary-font font-bold mb-3">Solde utilisable</h5>
                                <div class="flex align-items-center gap-2">
                                    <h1 class="secondary-font text-6xl">{{ souscription?.solde_utilisable !== undefined ? formatAmount(souscription.solde_utilisable) : 'N/A' }}</h1>
                                    <h2 class="secondary-font text-4xl mt-2">F</h2>
                                    <div class="mt-2 ml-2">
                                        <img src="/icons/icon-eye-off.svg" class="size-medium icon-white" />
                                    </div>
                                </div>
                                <div class="card-carte-floating-bottom">
                                    <h5 class="s10 secondary-font font-bold">*** *** **** 1212</h5>
                                    <div class="s10 flex-right align-items-center gap-2">
                                        <img src="/icons/icon-edit.svg" class="size-medium icon-white" />
                                        <h5 class="secondary-font font-bold">Commerce</h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <img src="/images/carte_coop_light.png" class="card-carte-img"/>
                    </div>
                    <!---->
                </div>
                <div class="s10_mobile px-4">
                    <!--CardCarte-->
                    <div class="card-carte">
                        <div class="card-carte-floating">
                            <div class="s20">
                                <h5 class="secondary-font font-bold mb-3">Numéro de carte</h5>
                                <div class="flex align-items-center gap-2">
                                    <h1 class="secondary-font text-6xl">***1212</h1>
                                </div>
                                <div class="card-carte-floating-bottom">
                                    <h5 class="s10 secondary-font font-bold">*** *** **** 1212</h5>
                                </div>
                            </div>
                        </div>
                        <img src="/images/carte_coop_verso.png" class="card-carte-img"/>
                    </div>
                    <!---->
                </div>
                <!-- <div class="flex-center">
                    <hr class="hr-vertical h-100p"/>
                </div> -->
                <div class="clear-both h-40"></div>

                <div class="grid3 px-6">
                    <!-- <div class="flex-center">
                        <hr class="hr-vertical h-100p"/>
                    </div> -->
                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Taux Interets Microfinance<br class="mb-1"/>
                        <b class="text-2xl text-grey-800">{{ souscription?.taux_interets !== undefined ? souscription.taux_interets + '%' : 'N/A' }}</b>
                        <hr/>
                    </h6>
                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Prélèvement sur salaire % <br class="mb-1"/>
                        <b class="text-2xl text-grey-800">{{ souscription?.prelevement_pourcent !== undefined ? souscription.prelevement_pourcent + '%' : 'N/A' }}</b>
                        <hr/>
                    </h6>
                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Prélèvement sur salaire<br class="mb-1"/>
                        <b class="text-2xl text-grey-800">{{ souscription?.prelevement_montant !== undefined ? formatAmount(souscription.prelevement_montant) : 'N/A' }}</b>
                        <hr/>
                    </h6>
                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Ans pour la Retraite<br class="mb-1"/>
                        <b class="text-2xl text-grey-800">{{ prospect.eta_retraite !== undefined ? Math.abs(prospect.eta_retraite) + ' ans' : 'N/A' }}</b>
                        <hr/>
                    </h6>
                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Quotité cessible max %<br class="mb-1"/>
                        <b class="text-2xl text-grey-800">{{ souscription?.quotite_cessible_max !== undefined ? souscription.quotite_cessible_max + '%' : 'N/A' }}</b>
                        <hr/>
                    </h6>

                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Quotité cessible max<br class="mb-1"/>
                        <b class="text-2xl text-grey-800">{{ prospect.quotite_cessible_montant !== undefined ? formatAmount(prospect.quotite_cessible_montant) : 'N/A' }}</b>
                        <hr/>
                    </h6>
                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Pret à Rembourser apres 3 Ans<br class="mb-1"/>
                        <b class="text-2xl text-grey-800">{{ souscription?.pret_rembourser !== undefined ? formatAmount(souscription.pret_rembourser) : 'N/A' }}</b>
                        <hr/>
                    </h6>
                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Cash-Back TVA Attendu<br class="mb-1"/>
                        <b class="text-2xl text-grey-800">{{ souscription?.cashback_tva !== undefined ? formatAmount(souscription.cashback_tva) : 'N/A' }} </b>
                        <hr/>
                    </h6>

                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Prêt en Concession<br class="mb-1">
                        <b class="text-2xl text-grey-800">{{ souscription?.pret_concession !== undefined ? formatAmount(souscription.pret_concession) : 'N/A' }}</b>
                        <hr>
                    </h6>
                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Prélèvement à la Solde<br class="mb-1">
                        <b class="text-2xl text-grey-800">{{ souscription?.prelevement_solde !== undefined ? formatAmount(souscription.prelevement_solde) : 'N/A' }}</b>
                    </h6>
                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Pret à Rembourser<br class="mb-1">
                        <b class="text-2xl text-grey-800">{{ souscription?.pret_rembourser_an !== undefined ? formatAmount(souscription.pret_rembourser_an) : 'N/A' }}</b>
                    </h6>
                    <!-- <h6 class="font-normal text-grey-500 text-center py-0">
                        Pouvoir Achat 1 An<br class="mb-1">
                        <b class="text-2xl text-grey-800">4 504 779,29</b>
                        <hr>
                    </h6> -->
                    <!-- <h6 class="font-normal text-grey-500 text-center py-0">
                        Pouvoir Achat 3 Ans<br class="mb-1">
                        <b class="text-2xl text-grey-800">13 514 337,86</b>
                    </h6> -->
                    <h6 class="font-normal text-grey-500 text-center py-0">
                        Durée Financement <br class="mb-1">
                        <b class="text-2xl text-grey-800">36 Mois</b>
                        - 
                        <b class="text-2xl text-grey-800">3 ans</b>

                    </h6>

                </div>

            </section>
        </section>

    </section>
</template>